import { Injectable } from "@angular/core";
import {
  Action,
  NgxsOnInit,
  Select,
  Selector,
  State,
  StateContext
} from "@ngxs/store";
import { UserMemberService } from "app/services/user-member.service";
import { Observable } from "rxjs";
import { filter, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { AuthState } from "~app/core/auth/core/auth.state";
import { InscriptionCommandService } from "../cotisation-form/inscription-command.service";
import { MemberRegistration } from "./member-registration.model";
import { YearSelectionFirebaseQueryService } from "./year-selection-firebase-query.service";
import { YearSelectionOdataQueryService } from "./year-selection-odata-query.service";
import { YearSelectionPeojectionQueryService } from "./year-selection-projection-query.service";
import { DownloadPdfDocument, LoadYearsInfos } from "./year-selection.actions";
import {
  AnnualRegistration,
  MemberClassEnum,
  MemberRegistrationSettings,
  YearInfo
} from "./year-selection.model";

export class YearSelectionStateModel {
  yearsInfos: YearInfo[] = [];
  memberId: string;
  firstDatePractice: Date;
  loading = true;
  memberRegistrationSettings: MemberRegistrationSettings;
  annualRegistrations: AnnualRegistration[];
  memberClass: MemberClassEnum;
  memberClassLoading = true;
  annualTemp: AnnualRegistration[];
  memberRegistrations: MemberRegistration[];
}

@State<YearSelectionStateModel>({
  name: "yearSelection",
  defaults: new YearSelectionStateModel(),
})
@Injectable()
export class YearSelectionState implements NgxsOnInit {
  @Select(AuthState.signedIn) signedIn$: Observable<boolean>;

  constructor(
    private firebaseQueryService: YearSelectionFirebaseQueryService,
    private userMemberService: UserMemberService,
    private odataQueryService: YearSelectionOdataQueryService,
    private projectionQueryService: YearSelectionPeojectionQueryService,
    private commandService: InscriptionCommandService
  ) { }

  @Selector()
  static yearsInfos(state: YearSelectionStateModel): YearInfo[] {
    return state.yearsInfos;
  }

  @Selector()
  static memberClass(state: YearSelectionStateModel): MemberClassEnum {
    return state.memberClass;
  }

  @Selector()
  static memberClassLoading(state: YearSelectionStateModel): boolean {
    return state.memberClassLoading;
  }

  @Selector()
  static loading(state: YearSelectionStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static memberRegistrationSettings(
    state: YearSelectionStateModel
  ): MemberRegistrationSettings {
    return state.memberRegistrationSettings;
  }

  @Selector()
  static annualRegistrations(
    state: YearSelectionStateModel
  ): AnnualRegistration[] {
    return state.annualRegistrations;
  }

  ngxsOnInit({ dispatch }: StateContext<YearSelectionStateModel>) {
    dispatch(new LoadYearsInfos())
  }

  @Action(DownloadPdfDocument)
  onDownloadPdfDocument(
    { getState }: StateContext<YearSelectionStateModel>,
    { documentId, year }: DownloadPdfDocument
  ) {
    return this.commandService.authorizeDocumentDownload(
      getState().memberId,
      documentId,
      `Résumé inscription ${year}.pdf`
    );
  }

  @Action(LoadYearsInfos)
  onLoadYearsInfos(
    { patchState, getState }: StateContext<YearSelectionStateModel>
  ) {
    this.userMemberService.memberId$
      .pipe(
        switchMap((memberId) => {
          patchState({ memberId });
          return this.odataQueryService.getMemberFirstDatePractice(getState().memberId)
        }),
        switchMap((firstDatePractice) => {
         patchState({ firstDatePractice });
          return this.odataQueryService.getAnnualRegistrations()
        }),
        switchMap((registrations) =>
          this.firebaseQueryService.getInscriptionInfosForYears(getState().memberId, getState().firstDatePractice, registrations)
        ),  
        mergeMap((yearsInfos) => {
          patchState({ yearsInfos, loading: false });
          return this.odataQueryService.getMemberRegistrationSettings(
            getState().memberId
          );
        }),
        map((settings) => patchState({ memberRegistrationSettings: settings }))
      )
      .subscribe();

    let annualRegistrationsPatch$ = this.odataQueryService
      .getAnnualRegistrations()
      .pipe(map((annualRegistrations) => patchState({ annualRegistrations })));

    this.signedIn$
      .pipe(
        filter((signedIn) => !!signedIn),
        switchMap((_) => annualRegistrationsPatch$)
      )
      .subscribe();

    this.userMemberService.memberId$
      .pipe(
        switchMap((memberId) =>
          this.projectionQueryService.getMemberClass(memberId)
        ),
        tap((res) =>
          patchState({ memberClass: res, memberClassLoading: false })
        )
      )
      .subscribe();
  }
}
